import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tenantAdminService from '../../services/tenantAdminService';
import { TenantAdmin, TenantDbVersion } from '../../types';
import { getApiErrorMessage } from '../../utils/apiError';
import TenantLegacyMigrationDialog from '../../components/sistema/TenantLegacyMigrationDialog';

const Tenants: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tenants, setTenants] = useState<TenantAdmin[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [dbVersions, setDbVersions] = useState<Record<number, TenantDbVersion>>({});
  const [migrateTenant, setMigrateTenant] = useState<TenantAdmin | null>(null);
  const [migrateJobId, setMigrateJobId] = useState<string | null>(null);

  const prevQueryRef = useRef({ searchTerm, page, rowsPerPage });

  const fetchTenants = useCallback(
    async (pageNum: number, pageSize: number, search: string) => {
      try {
        setLoading(true);
        const data = await tenantAdminService.listPaged({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search.trim() || undefined,
        });
        setTenants(data.items);
        setTotalCount(data.totalCount);
        void loadDbVersions(data.items);
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        const message =
          status === 404
            ? 'El servidor no expone /api/admin/tenants. Falta desplegar el módulo de tenants en la API.'
            : getApiErrorMessage(error, 'Error al cargar tenants');
        enqueueSnackbar(message, { variant: 'error', preventDuplicate: true });
        setTenants([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const loadDbVersions = async (items: TenantAdmin[]) => {
    const entries = await Promise.all(
      items.map(async (tenant): Promise<[number, TenantDbVersion]> => {
        try {
          const version = await tenantAdminService.getDbVersion(tenant.id);
          return [tenant.id, version];
        } catch (error: unknown) {
          const fallback: TenantDbVersion = {
            version: 'Unknown',
            reasons: [getApiErrorMessage(error, 'No se pudo diagnosticar la BD.')],
          };
          return [tenant.id, fallback];
        }
      }),
    );
    setDbVersions((prev) => {
      const next = { ...prev };
      entries.forEach(([id, version]) => {
        next[id] = version;
      });
      return next;
    });
  };

  useEffect(() => {
    const prev = prevQueryRef.current;
    const searchChanged = prev.searchTerm !== searchTerm;
    prevQueryRef.current = { searchTerm, page, rowsPerPage };

    const delay = searchChanged ? 300 : 0;

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      void fetchTenants(page, rowsPerPage, searchTerm);
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [page, rowsPerPage, searchTerm, fetchTenants]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleToggleStatus = async (tenant: TenantAdmin) => {
    const nextActive = !tenant.isActive;
    if (!nextActive) {
      const ok = window.confirm(
        `¿Desactivar el tenant "${tenant.name}" (${tenant.code})? Los usuarios no podrán operar en él.`,
      );
      if (!ok) return;
    }

    try {
      await tenantAdminService.setActiveStatus(tenant.id, nextActive);
      enqueueSnackbar(
        nextActive ? 'Tenant activado correctamente' : 'Tenant desactivado correctamente',
        { variant: 'success' },
      );
      fetchTenants(page, rowsPerPage, searchTerm);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cambiar el estado del tenant'), {
        variant: 'error',
      });
    }
  };

  const handleMigrate = async (tenant: TenantAdmin) => {
    const secondaryHint = tenant.parentTenantCode
      ? ` Es hija de ${tenant.parentTenantCode}. Convertí primero la madre si aún no es eOne.`
      : '';
    const ok = window.confirm(
      `¿Migrar la base de datos del tenant "${tenant.name}" (${tenant.code}) a eOne? El proceso modifica el esquema de esa BD y puede demorar.${secondaryHint}`,
    );
    if (!ok) return;

    try {
      const started = await tenantAdminService.startMigrateToWeb(tenant.id);
      setMigrateTenant(tenant);
      setMigrateJobId(started.jobId);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo iniciar la migración'), {
        variant: 'error',
      });
    }
  };

  const handleMigrationSucceeded = useCallback((tenant: TenantAdmin) => {
    enqueueSnackbar(`Migración de ${tenant.code} completada correctamente.`, { variant: 'success' });
    void loadDbVersions([tenant]);
  }, [enqueueSnackbar]);

  const renderDbVersionChip = (tenant: TenantAdmin) => {
    const info = dbVersions[tenant.id];
    if (!info) {
      return <Chip label="Diagnosticando..." size="small" variant="outlined" />;
    }

    if (info.version === 'eOne') {
      const eOneLabel = tenant.parentTenantCode
        ? `eOne · hija de ${tenant.parentTenantCode}`
        : 'eOne';
      return (
        <Tooltip title={info.reasons.join(' ')}>
          <span>
            <Chip label={eOneLabel} size="small" color="primary" />
          </span>
        </Tooltip>
      );
    }

    if (info.version === 'OneLegacy') {
      const label = tenant.parentTenantCode
        ? `One Legacy · hija de ${tenant.parentTenantCode}`
        : 'One Legacy';
      return (
        <Tooltip title={info.reasons.join(' ')}>
          <span>
            <Chip label={label} size="small" color="warning" />
          </span>
        </Tooltip>
      );
    }

    const tooltip = info.reasons.join(' ') || 'No se pudo determinar la versión';
    return (
      <Tooltip title={tooltip}>
        <span>
          <Chip label="Desconocida" size="small" variant="outlined" />
        </span>
      </Tooltip>
    );
  };

  const renderStatusChip = (isActive: boolean) => (
    <Chip
      label={isActive ? 'Activo' : 'Inactivo'}
      size="small"
      color={isActive ? 'success' : 'default'}
      variant={isActive ? 'filled' : 'outlined'}
    />
  );

  const renderActions = (tenant: TenantAdmin) => {
    const version = dbVersions[tenant.id]?.version;
    const hasParent = Boolean(tenant.parentTenantId);
    const canMigrate = version === 'OneLegacy' || (version === 'eOne' && hasParent);

    return (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        <Tooltip title="Editar">
          <IconButton
            size="small"
            color="primary"
            onClick={() => navigate(`/sistema/tenants/${tenant.id}`)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            canMigrate
              ? 'Migrar a eOne'
              : version === 'eOne'
                ? 'Ya está en versión eOne'
                : 'Migrar a eOne (solo para One Legacy)'
          }
        >
          <span>
            <IconButton
              size="small"
              color="secondary"
              disabled={!canMigrate}
              onClick={() => handleMigrate(tenant)}
            >
              <UpgradeIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={tenant.isActive ? 'Desactivar' : 'Activar'}>
          <IconButton
            size="small"
            color={tenant.isActive ? 'warning' : 'success'}
            onClick={() => handleToggleStatus(tenant)}
          >
            {tenant.isActive ? <BlockIcon /> : <CheckCircleIcon />}
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Administración de Tenants
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/sistema/tenants/nuevo')}
            >
              Nuevo tenant
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código o nombre..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                },
              }}
            />
          </Box>

          {!loading && tenants.length === 0 ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                {searchTerm.trim()
                  ? 'No se encontraron tenants con ese criterio de búsqueda.'
                  : 'No hay tenants registrados en el sistema.'}
              </Typography>
              {!searchTerm.trim() && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/sistema/tenants/nuevo')}
                >
                  Crear el primer tenant
                </Button>
              )}
            </Stack>
          ) : isMobile ? (
            <Stack spacing={2}>
              {tenants.map((tenant) => (
                <Card key={tenant.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {tenant.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Código: {tenant.code}
                      {tenant.parentTenantCode ? ` · hija de ${tenant.parentTenantCode}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {tenant.id}
                    </Typography>
                    {renderStatusChip(tenant.isActive)}
                    {renderDbVersionChip(tenant)}
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {renderActions(tenant)}
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Versión BD</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id} hover>
                      <TableCell>{tenant.id}</TableCell>
                      <TableCell>{tenant.code}</TableCell>
                      <TableCell>
                        {tenant.name}
                        {tenant.parentTenantCode ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Hija de {tenant.parentTenantCode}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{renderStatusChip(tenant.isActive)}</TableCell>
                      <TableCell>{renderDbVersionChip(tenant)}</TableCell>
                      <TableCell align="center">{renderActions(tenant)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalCount > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          )}
        </CardContent>
      </Card>
      <TenantLegacyMigrationDialog
        open={Boolean(migrateTenant && migrateJobId)}
        tenant={migrateTenant}
        jobId={migrateJobId}
        onClose={() => {
          setMigrateTenant(null);
          setMigrateJobId(null);
        }}
        onSucceeded={handleMigrationSucceeded}
      />
    </Box>
  );
};

export default Tenants;
